import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Alert, Button, DatePicker, Empty, Progress, Result, Space, Spin, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined, EyeOutlined, InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { useFirebaseAuth } from "../context/AuthContext";
import { getAlertStatsReport } from "../context/firebaseConfig";
import { getPrj } from "../constants/methods";
import '../assets/css/main.css'

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

// Configuración funcional: proyectos, servicios y ventana de trazabilidad fiable.
// Proyectos donde las Cloud Functions ya escriben las trazas st... que alimentan este panel (Caser).
const ALLOWED_PROJECTS = ['pus', 'ahp', 'rep'];
// Servicios estadísticos usados para filtrar Firestore: AHP incluye etiquetas funcionales AUT y PUS.
const AHP_STATS_SERVICES = ['AHP', 'AUT', 'PUS'];
const STATS_SERVICES_BY_PROJECT = {
    ahp: AHP_STATS_SERVICES,
    pus: AHP_STATS_SERVICES,
    rep: ['REP']
};
const STATS_SERVICE_LABEL_BY_PROJECT = {
    ahp: 'AHP',
    pus: 'AHP',
    rep: 'REP'
};
const STATS_SERVICES_BY_ALIAS = {
    AHP: AHP_STATS_SERVICES,
    AUT: AHP_STATS_SERVICES,
    PUS: AHP_STATS_SERVICES,
    REP: ['REP']
};
// Fecha del despliegue completo de trazabilidad; antes de este día el panel puede necesitar fallbacks.
const TRACE_START = dayjs('2026-07-14').startOf('day');
// Etiqueta visible del aviso de fiabilidad, separada para no depender del formato interno de dayjs.
const TRACE_START_LABEL = '14/07/2026';
// Zona horaria usada si el usuario no trae una configuración específica.
const DEFAULT_TIMEZONE = 'Europe/Madrid';

const normalizeStatsService = value => value ? `${value}`.toUpperCase() : '';

const getAlertStatsServiceContext = (project, userService) => {
    const normalizedProject = `${project || ''}`.toLowerCase();
    const normalizedUserService = normalizeStatsService(userService);
    const filter = STATS_SERVICES_BY_PROJECT[normalizedProject] || STATS_SERVICES_BY_ALIAS[normalizedUserService] || [normalizedUserService].filter(Boolean);
    const label = STATS_SERVICE_LABEL_BY_PROJECT[normalizedProject] || filter.join(' / ');
    return { filter, label };
};

// Configuración visual y de traducciones para tarjetas, comparativas y ayudas.
const tagColors = {
    sent: 'green',
    error: 'red',
    queued: 'gold',
    processing: 'blue',
    missing: 'default',
    unknown: 'default'
};

const sections = [
    {
        titleKey: 'ALERT_STATS_SECTION_ALERTS',
        helpKey: 'alerts',
        items: [
            { key: 'alerts.generated', labelKey: 'ALERT_STATS_CARD_ALERTS_GENERATED', tone: 'highlight' },
            { key: 'alerts.noPolicies', labelKey: 'ALERT_STATS_CARD_NO_POLICIES', tone: 'secondary' },
            { key: 'alerts.papOk', labelKey: 'ALERT_STATS_CARD_PAP_OK', tone: 'secondary' },
            { key: 'alerts.papIncidents', labelKey: 'ALERT_STATS_CARD_PAP_ERRORS', tone: 'secondary' },
            { key: 'alerts.papIncidentsResolved', labelKey: 'ALERT_STATS_CARD_PAP_ERRORS_RESOLVED' },
            { key: 'alerts.papIncidentsUnresolved', labelKey: 'ALERT_STATS_CARD_PAP_ERRORS_UNRESOLVED' },
            { key: 'alerts.mailAfterTenTries', labelKey: 'ALERT_STATS_CARD_ALERTS_MAIL10' }
        ]
    },
    {
        titleKey: 'ALERT_STATS_SECTION_CANCELLATIONS',
        helpKey: 'cancellations',
        items: [
            { key: 'cancellations.total', labelKey: 'ALERT_STATS_CARD_TOTAL', tone: 'highlight' },
            { key: 'cancellations.noPolicies', labelKey: 'ALERT_STATS_CARD_NO_POLICIES', tone: 'secondary' },
            { key: 'cancellations.papOk', labelKey: 'ALERT_STATS_CARD_PAP_OK', tone: 'secondary' },
            { key: 'cancellations.beforePap', labelKey: 'ALERT_STATS_CARD_CANCELLATIONS_NO_PAP_NOTIFICATION', tone: 'secondary' },
            { key: 'cancellations.papIncidents', labelKey: 'ALERT_STATS_CARD_PAP_ERRORS', tone: 'secondary' },
            { key: 'cancellations.papIncidentsUnresolved', labelKey: 'ALERT_STATS_CARD_PAP_ERRORS_UNRESOLVED' },
            { key: 'cancellations.papErrorMail', labelKey: 'ALERT_STATS_CARD_CANCELLATIONS_PAP_ERROR_MAIL' }
        ]
    },
    {
        titleKey: 'ALERT_STATS_SECTION_CLOSURES',
        helpKey: 'closures',
        items: [
            { key: 'closures.total', labelKey: 'ALERT_STATS_CARD_TOTAL', tone: 'highlight' },
            { key: 'closures.manual', labelKey: 'ALERT_STATS_CARD_CLOSURES_MANUAL' },
            { key: 'closures.auto', labelKey: 'ALERT_STATS_CARD_CLOSURES_AUTO' }
        ]
    },
    {
        titleKey: 'ALERT_STATS_SECTION_MAILS',
        helpKey: 'mails',
        items: [
            { key: 'mails.traced', labelKey: 'ALERT_STATS_CARD_MAILS_DETECTED', tone: 'highlight' },
            { key: 'mails.sent', labelKey: 'ALERT_STATS_CARD_MAILS_SENT' },
            { key: 'mails.error', labelKey: 'ALERT_STATS_CARD_MAILS_ERROR' },
            { key: 'mails.pending', labelKey: 'ALERT_STATS_CARD_MAILS_PENDING' },
            { key: 'mails.missingDoc', labelKey: 'ALERT_STATS_CARD_MAILS_MISSING_DOC' }
        ]
    },
    {
        titleKey: 'ALERT_STATS_SECTION_QUALITY',
        helpKey: 'quality',
        items: [
            { key: 'quality.totalEvents', labelKey: 'ALERT_STATS_CARD_QUALITY_EVENTS', tone: 'highlight' },
            { key: 'quality.reliabilityPct', labelKey: 'ALERT_STATS_CARD_QUALITY_RELIABILITY', suffix: '%' }
        ]
    }
];

const comparisonGroups = [
    {
        key: 'alerts',
        labelKey: 'ALERT_STATS_SECTION_ALERTS',
        itemKeys: ['alertPapOk', 'alertNoPap', 'alertResolved']
    },
    {
        key: 'cancellations',
        labelKey: 'ALERT_STATS_SECTION_CANCELLATIONS',
        itemKeys: ['cancelPapOk', 'cancelNoPap']
    }
];

const comparisonLabelKeys = {
    alertPapOk: 'ALERT_STATS_COMPARISON_ALERT_PAP_OK',
    alertNoPap: 'ALERT_STATS_COMPARISON_ALERT_NO_PAP',
    alertResolved: 'ALERT_STATS_COMPARISON_ALERT_RESOLVED',
    cancelPapOk: 'ALERT_STATS_COMPARISON_CANCEL_PAP_OK',
    cancelNoPap: 'ALERT_STATS_COMPARISON_CANCEL_NO_PAP'
};

const sectionHelp = {
    quality: {
        titleKey: 'ALERT_STATS_HELP_QUALITY_TITLE',
        items: [
            'ALERT_STATS_HELP_QUALITY_EVENTS',
            'ALERT_STATS_HELP_QUALITY_RELIABILITY'
        ]
    },
    alerts: {
        titleKey: 'ALERT_STATS_HELP_ALERTS_TITLE',
        items: [
            'ALERT_STATS_HELP_ALERTS_GENERATED',
            'ALERT_STATS_HELP_ALERTS_NO_POLICIES',
            'ALERT_STATS_HELP_ALERTS_PAP_OK',
            'ALERT_STATS_HELP_ALERTS_ERRORS',
            'ALERT_STATS_HELP_ALERTS_ERRORS_RESOLVED',
            'ALERT_STATS_HELP_ALERTS_ERRORS_UNRESOLVED',
            'ALERT_STATS_HELP_ALERTS_MAIL10'
        ]
    },
    cancellations: {
        titleKey: 'ALERT_STATS_HELP_CANCELLATIONS_TITLE',
        items: [
            'ALERT_STATS_HELP_CANCELLATIONS_TOTAL',
            'ALERT_STATS_HELP_CANCELLATIONS_NO_POLICIES',
            'ALERT_STATS_HELP_CANCELLATIONS_PAP_OK',
            'ALERT_STATS_HELP_CANCELLATIONS_BEFORE_PAP',
            'ALERT_STATS_HELP_CANCELLATIONS_ERRORS',
            'ALERT_STATS_HELP_CANCELLATIONS_ERRORS_UNRESOLVED',
            'ALERT_STATS_HELP_CANCELLATIONS_MAIL_ERROR'
        ]
    },
    closures: {
        titleKey: 'ALERT_STATS_HELP_CLOSURES_TITLE',
        items: [
            'ALERT_STATS_HELP_CLOSURES_TOTAL',
            'ALERT_STATS_HELP_CLOSURES_MANUAL',
            'ALERT_STATS_HELP_CLOSURES_AUTO'
        ]
    },
    mails: {
        titleKey: 'ALERT_STATS_HELP_MAILS_TITLE',
        items: [
            'ALERT_STATS_HELP_MAILS_DETECTED',
            'ALERT_STATS_HELP_MAILS_SENT',
            'ALERT_STATS_HELP_MAILS_ERROR',
            'ALERT_STATS_HELP_MAILS_PENDING',
            'ALERT_STATS_HELP_MAILS_MISSING_DOC'
        ]
    }
};

// Mapeos de eventos del backend a textos traducidos del panel y del CSV.
const eventLabels = {
    papAlertOk: 'PAP_REPORT_EVENT_PAP_ALERT_OK',
    papAlertErr: 'PAP_REPORT_EVENT_PAP_ALERT_ERROR',
    papAlertMail10: 'PAP_REPORT_EVENT_PAP_ALERT_MAIL10',
    noPolicyAlertMail: 'PAP_REPORT_EVENT_NO_POLICIES_ALERT_MAIL',
    cancelBeforePap: 'PAP_REPORT_EVENT_CANCEL_BEFORE_PAP',
    papCancelOk: 'PAP_REPORT_EVENT_PAP_CANCEL_OK',
    papCancelErr: 'PAP_REPORT_EVENT_PAP_CANCEL_ERROR',
    papCancelMail: 'PAP_REPORT_EVENT_PAP_CANCEL_MAIL',
    noPolicyCancelMail: 'PAP_REPORT_EVENT_NO_POLICIES_CANCEL_MAIL',
    closed: 'ALERT_STATS_EVENT_CLOSED',
    historical: 'PAP_REPORT_EVENT_HISTORICAL'
};

const eventGroups = {
    Alerta: 'PAP_REPORT_GROUP_ALERT',
    Cancelacion: 'PAP_REPORT_GROUP_CANCELLATION',
    'Email alerta': 'PAP_REPORT_GROUP_ALERT_EMAIL',
    'Email cancelacion': 'PAP_REPORT_GROUP_CANCELLATION_EMAIL',
    Cierre: 'PAP_REPORT_GROUP_CLOSING',
    Historico: 'PAP_REPORT_GROUP_HISTORICAL'
};

const mailTypeLabels = {
    pap_alert_10_attempts: 'PAP_REPORT_MAIL_TYPE_PAP_ALERT_10_ATTEMPTS',
    pap_cancel_error: 'PAP_REPORT_MAIL_TYPE_PAP_CANCEL_ERROR',
    no_policies_alert: 'PAP_REPORT_MAIL_TYPE_NO_POLICIES_ALERT',
    no_policies_cancel: 'PAP_REPORT_MAIL_TYPE_NO_POLICIES_CANCEL'
};

// Utilidades de formato, exportación y representación de etiquetas.
const getValue = (data, path) => path.split('.').reduce((acc, key) => acc?.[key], data) || 0;
const hasRole = (user, role) => user?.rol?.indexOf(role) > -1;
const pct = (value, total) => total ? Math.round((value / total) * 100) : 0;
const ratio = (value, total) => `${value || 0}/${total || 0}`;

const dateValue = value => {
    if (!value) return null;
    if (typeof value === 'number') return value;
    if (value?.seconds) return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    return null;
}

const translateKey = (t, key, fallback = '') => key ? t(key, { defaultValue: fallback || key }) : fallback;

const formatDateTime = (value, timezone, locale = 'es-ES') => {
    const millis = dateValue(value);
    if (!millis) return '-';
    return new Intl.DateTimeFormat(locale, {
        timeZone: timezone || DEFAULT_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(new Date(millis));
}

const csvCell = value => {
    const normalized = value === null || value === undefined ? '' : String(value);
    return `"${normalized.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
}

const downloadCsvFile = ({ filename, headers, rows }) => {
    const lines = rows.map(row => row.map(csvCell).join(';'));
    const csv = `\uFEFF${headers.map(csvCell).join(';')}\n${lines.join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

const isFallbackEvent = row => (
    row.eventType === 'historical' ||
    (row.eventType === 'papAlertMail10' && !row.stPapAlertMail) ||
    (row.eventType === 'noPolicyAlertMail' && !row.stNoPolAlertMail) ||
    (row.eventType === 'noPolicyCancelMail' && !row.stNoPolCancelMail)
);

const traceabilityLabel = (row, t) => {
    if (row.eventType === 'historical') return t('PAP_REPORT_TRACE_HISTORICAL');
    if (isFallbackEvent(row)) return t('PAP_REPORT_TRACE_FALLBACK');
    return t('PAP_REPORT_TRACE_FLAGS');
}

const traceabilityTag = (row, t) => {
    if (row.eventType === 'historical') return <Tag>{traceabilityLabel(row, t)}</Tag>;
    if (isFallbackEvent(row)) return <Tag color="gold">{traceabilityLabel(row, t)}</Tag>;
    return <Tag color="blue">{traceabilityLabel(row, t)}</Tag>;
}

const resultTag = (row, t) => {
    if (['papAlertOk', 'papCancelOk'].includes(row.eventType)) return <Tag color="green">OK</Tag>;
    if (['papAlertErr', 'papCancelErr'].includes(row.eventType)) {
        if ((row.eventType === 'papAlertErr' && row.stPapAlertOk) || (row.eventType === 'papCancelErr' && row.stPapCancelOk)) {
            return <Tag color="blue">{t('ALERT_STATS_RESULT_INCIDENT_RESOLVED')}</Tag>;
        }
        return <Tag color="red">{t('ALERT_STATS_RESULT_INCIDENT_UNRESOLVED')}</Tag>;
    }
    if (row.hasMail) return <Tag color="orange">{t('ALERT_STATS_RESULT_EMAIL')}</Tag>;
    if (row.eventType === 'cancelBeforePap') return <Tag>{t('ALERT_STATS_RESULT_PAP_SKIPPED')}</Tag>;
    if (row.eventType === 'closed') return <Tag color={row.stClosedBy === 'auto' ? 'blue' : 'purple'}>{row.stClosedBy === 'auto' ? t('ALERT_STATS_RESULT_AUTO') : t('ALERT_STATS_RESULT_MANUAL')}</Tag>;
    return <Tag>{t('ALERT_STATS_RESULT_HISTORICAL')}</Tag>;
}

const policyLabel = (row, t) => {
    if (row.eventType === 'noPolicyAlertMail') return t('PAP_REPORT_POLICY_NO_POLICIES_ALERT');
    if (row.eventType === 'noPolicyCancelMail') return t('PAP_REPORT_POLICY_NO_POLICIES_CANCEL');
    if (['papAlertOk', 'papAlertErr', 'papAlertMail10', 'papCancelOk', 'papCancelErr', 'papCancelMail'].includes(row.eventType)) {
        return t('PAP_REPORT_POLICY_WITH_POLICIES');
    }
    if (row.eventType === 'cancelBeforePap') return t('PAP_REPORT_POLICY_NOT_EVALUATED');
    return t('PAP_REPORT_POLICY_NOT_INFERABLE');
}

const policyTag = (row, t) => {
    if (row.eventType === 'noPolicyAlertMail') return <Tag color="red">{policyLabel(row, t)}</Tag>;
    if (row.eventType === 'noPolicyCancelMail') return <Tag color="red">{policyLabel(row, t)}</Tag>;
    if (['papAlertOk', 'papAlertErr', 'papAlertMail10', 'papCancelOk', 'papCancelErr', 'papCancelMail'].includes(row.eventType)) {
        return <Tag color="green">{policyLabel(row, t)}</Tag>;
    }
    return <Tag>{policyLabel(row, t)}</Tag>;
}

const mailStatusLabel = (row, t) => {
    if (!row.hasMail) return '-';
    const labels = {
        sent: t('PAP_REPORT_MAIL_STATUS_SENT'),
        error: t('PAP_REPORT_MAIL_STATUS_ERROR'),
        queued: t('PAP_REPORT_MAIL_STATUS_QUEUED'),
        processing: t('PAP_REPORT_MAIL_STATUS_PROCESSING'),
        missing: t('PAP_REPORT_MAIL_STATUS_MISSING'),
        unknown: t('PAP_REPORT_MAIL_STATUS_UNKNOWN')
    };
    return labels[row.mailStatus?.status] || row.mailStatus?.label || t('PAP_REPORT_MAIL_STATUS_QUEUED');
}

const helpText = (text) => {
    const parts = `${text}`.split(':');
    if (parts.length < 2) return text;
    const title = parts.shift();
    return (
        <>
            <Text strong style={{ color: 'inherit' }}>{title}:</Text>
            {parts.join(':')}
        </>
    );
}

const helpTooltipContent = (help, t) => (
    <div style={{ maxWidth: 'min(520px, calc(100vw - 48px))' }}>
        <Text strong style={{ color: 'inherit' }}>{t(help.titleKey)}</Text>
        <ul style={{ paddingLeft: 18, margin: '8px 0 0' }}>
            {(help.items || []).map(item => (
                <li key={item} style={{ marginBottom: 6 }}>
                    {helpText(t(item))}
                </li>
            ))}
        </ul>
    </div>
);

// Estilos reutilizables del panel (responsive); mantienen el layout adaptable sin tocar el scroll horizontal de la tabla.
const metricCardBaseStyle = {
    border: '1px solid #e6e8ec',
    borderRadius: 6,
    padding: 14,
    background: '#fff',
    boxShadow: 'none',
    outline: 'none'
};

const metricCardToneStyles = {
    highlight: {
        border: '1px solid #91caff',
        background: '#e6f4ff'
    },
    secondary: {
        border: '1px solid #bae0ff',
        background: '#f0f7ff'
    }
};

const pageStyle = {
    width: '92%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    gap: 18,
    paddingTop: 30,
    paddingRight: 'clamp(12px, 2vw, 20px)'
};

const metricGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))',
    gap: 12
};

const comparisonGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    columnGap: 32,
    rowGap: 18,
    marginTop: 8
};

const eventHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 50,
    paddingRight: 12
};

const exportButtonStyle = {
    marginBottom: 18
};

const metricTitle = label => (
    <div style={{ minHeight: 42, display: 'flex', alignItems: 'flex-start', lineHeight: 1.45 }}>
        {label}
    </div>
);

// Componentes de presentación reutilizados por los bloques de métricas y comparativas.
const MetricSection = ({ title, items, summary, help, t, marginTop = 22 }) => (
    <section style={{ marginTop }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Title level={4} style={{ margin: 0 }}>{title}</Title>
            {help && (
                <Tooltip
                    title={helpTooltipContent(help, t)}
                    color="#fff"
                    placement="rightTop"
                    overlayStyle={{ maxWidth: 'min(560px, calc(100vw - 32px))' }}
                    overlayInnerStyle={{
                        color: '#262626',
                        border: '1px solid #d9d9d9',
                        padding: '2em',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                    }}
                >
                    <span
                        aria-label={t('ALERT_STATS_HELP_BUTTON')}
                        style={{ display: 'inline-flex', color: '#262626', cursor: 'help', lineHeight: 1 }}
                    >
                        <InfoCircleOutlined />
                    </span>
                </Tooltip>
            )}
        </div>
        <div style={metricGridStyle}>
            {items.map(item => (
                <div key={item.key} style={{ ...metricCardBaseStyle, ...(metricCardToneStyles[item.tone] || {}) }}>
                    <Statistic title={metricTitle(t(item.labelKey))} value={getValue(summary, item.key)} suffix={item.suffix} />
                </div>
            ))}
        </div>
    </section>
);

const ComparisonSection = ({ comparisons, t }) => {
    const groups = comparisonGroups
        .map(group => ({
            ...group,
            items: group.itemKeys
                .map(itemKey => comparisons.find(item => item.key === itemKey))
                .filter(Boolean)
        }))
        .filter(row => row.items.length);

    return (
        <section style={{ border: '1px solid #e6e8ec', borderRadius: 6, padding: 16, background: '#fff' }}>
            <Title level={4} style={{ marginTop: 0 }}>{t('ALERT_STATS_COMPARISONS_TITLE')}</Title>
            <div style={{ display: 'grid', gap: 20 }}>
                {groups.map(row => (
                    <div key={row.key}>
                        <Text strong>{t(row.labelKey)}</Text>
                        <div style={comparisonGridStyle}>
                            {row.items.map(item => (
                                <div key={item.key}>
                                    <Text strong>{t(comparisonLabelKeys[item.key])}</Text>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(76px, max-content) minmax(160px, 1fr)', gap: 12, alignItems: 'center', marginTop: 6 }}>
                                        <Text strong style={{ color: '#1677ff', whiteSpace: 'nowrap' }}>{ratio(item.value, item.total)}</Text>
                                        <Progress percent={pct(item.value, item.total)} showInfo={false} strokeColor="#1677ff" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

// COMPONENTE => Vista principal: carga datos, exporta CSV y renderiza resumen funcional + tabla de eventos.
const AlertStatsReport = () => {
    const { t, i18n } = useTranslation();
    const [user] = useState(useFirebaseAuth());
    // const [range, setRange] = useState([dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')]); // inicio de intervalo 1 semana
    const [range, setRange] = useState([dayjs().subtract(1, 'day').startOf('day'), dayjs().endOf('day')]);    // inicio de intervalo día de antes
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState({ events: [], summary: {}, comparisons: [] });
    const [error, setError] = useState('');
    const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 25 });
    const nav = useNavigate();
    const project = getPrj();
    const timezone = user?.configs?.timezone || DEFAULT_TIMEZONE;
    const statsService = useMemo(() => getAlertStatsServiceContext(project, user?.service), [project, user?.service]);
    const canAccess = ALLOWED_PROJECTS.includes(project) && (hasRole(user, 'ADMIN') || hasRole(user, 'MANAGEMENT'));
    const includesHistoricalRange = range?.[0]?.startOf('day').valueOf() < TRACE_START.valueOf();
    const dateLocale = i18n.language?.indexOf('en') === 0 ? 'en-GB' : 'es-ES';

    const loadReport = useCallback(async () => {
        if (!canAccess || !range?.[0] || !range?.[1]) return;
        setLoading(true);
        setError('');
        try {
            const data = await getAlertStatsReport({
                service: statsService.filter,
                from: range[0].startOf('day').valueOf(),
                to: range[1].endOf('day').valueOf(),
                includeMail: true
            });
            setReport(data);
            setTablePagination(prev => ({ ...prev, current: 1 }));
        } catch (e) {
            console.error(e);
            setError(e?.message || t('ALERT_STATS_LOAD_ERROR'));
        } finally {
            setLoading(false);
        }
    }, [canAccess, range, statsService, t]);

    const openWatch = useCallback((row) => {
        if (!row?.alertId || !row?.phone) return;
        nav('/watch', { state: { data: row, type: row.coll || 'alerts', petet: 'si' } });
    }, [nav]);

    const downloadCsv = useCallback(() => {
        if (!report.events.length) return;
        const headers = [
            t('PAP_REPORT_COLUMN_EVENT_DATE'),
            t('PAP_REPORT_COLUMN_ALERT_DATE'),
            t('PAP_REPORT_COLUMN_COLLECTION'),
            t('LABEL_PHONE'),
            t('PAP_REPORT_COLUMN_GROUP'),
            t('PAP_REPORT_COLUMN_PATH'),
            t('ALERT_STATS_COLUMN_RESULT'),
            t('PAP_REPORT_COLUMN_POLICIES'),
            t('PAP_REPORT_COLUMN_TRACEABILITY'),
            t('PAP_REPORT_COLUMN_DETAIL'),
            t('PAP_REPORT_COLUMN_MAIL_TYPE'),
            t('PAP_REPORT_COLUMN_MAIL_STATUS'),
            t('PAP_REPORT_COLUMN_MAIL_DATE'),
            t('PAP_REPORT_COLUMN_MAIL_ID'),
            t('ALERT_STATS_COLUMN_ALERT_ID')
        ];
        const rows = report.events.map(row => [
            formatDateTime(row.eventAt, timezone, dateLocale),
            formatDateTime(Number(row.alertId), timezone, dateLocale),
            row.coll || '',
            row.phone || '',
            translateKey(t, eventGroups[row.eventGroup], row.eventGroup || ''),
            translateKey(t, eventLabels[row.eventType], row.eventLabel || ''),
            row.eventType || '',
            row.eventType?.indexOf('noPolicy') > -1 ? t('ALERT_STATS_CSV_NO_POLICIES') : '',
            traceabilityLabel(row, t),
            row.detail || '',
            translateKey(t, mailTypeLabels[row.eventMailType], row.eventMailType || ''),
            row.hasMail ? mailStatusLabel(row, t) : '',
            formatDateTime(row.mailStatus?.at || row.eventMailQueuedAt, timezone, dateLocale),
            row.eventMailId || '',
            row.alertId || ''
        ]);
        const from = range?.[0]?.format('YYYYMMDD') || t('ALERT_STATS_FILE_START');
        const to = range?.[1]?.format('YYYYMMDD') || t('ALERT_STATS_FILE_END');
        downloadCsvFile({ filename: `${t('ALERT_STATS_FILE_PREFIX')}-${from}-${to}.csv`, headers, rows });
    }, [dateLocale, range, report.events, t, timezone]);

    const downloadSummaryCsv = useCallback(() => {
        if (!report.events.length) return;
        const summary = report.summary || {};
        const comparisons = report.comparisons || [];
        const headers = [
            t('ALERT_STATS_SUMMARY_CSV_SECTION'),
            t('ALERT_STATS_SUMMARY_CSV_METRIC'),
            t('ALERT_STATS_SUMMARY_CSV_VALUE'),
            t('ALERT_STATS_SUMMARY_CSV_TOTAL'),
            t('ALERT_STATS_SUMMARY_CSV_PERCENT'),
            t('ALERT_STATS_SUMMARY_CSV_DETAIL')
        ];
        const contextRows = [
            [t('ALERT_STATS_SUMMARY_CONTEXT_SECTION'), t('LABEL_SERVICE'), statsService.label || '', '', '', ''],
            [t('ALERT_STATS_SUMMARY_CONTEXT_SECTION'), t('ALERT_STATS_SUMMARY_FROM'), formatDateTime(range?.[0]?.startOf('day').valueOf(), timezone, dateLocale), '', '', ''],
            [t('ALERT_STATS_SUMMARY_CONTEXT_SECTION'), t('ALERT_STATS_SUMMARY_TO'), formatDateTime(range?.[1]?.endOf('day').valueOf(), timezone, dateLocale), '', '', '']
        ];
        const comparisonRows = comparisonGroups.flatMap(group => group.itemKeys
            .map(itemKey => comparisons.find(item => item.key === itemKey))
            .filter(Boolean)
            .map(item => [
                t('ALERT_STATS_COMPARISONS_TITLE'),
                t(comparisonLabelKeys[item.key]),
                item.value || 0,
                item.total || 0,
                `${pct(item.value, item.total)}%`,
                t(group.labelKey)
            ]));
        const cardRows = sections.flatMap(section => section.items.map(item => [
            t(section.titleKey),
            t(item.labelKey),
            getValue(summary, item.key),
            '',
            '',
            t('ALERT_STATS_SUMMARY_DETAIL_CARD')
        ]));
        const from = range?.[0]?.format('YYYYMMDD') || t('ALERT_STATS_FILE_START');
        const to = range?.[1]?.format('YYYYMMDD') || t('ALERT_STATS_FILE_END');
        downloadCsvFile({
            filename: `${t('ALERT_STATS_SUMMARY_FILE_PREFIX')}-${from}-${to}.csv`,
            headers,
            rows: [...contextRows, ...comparisonRows, ...cardRows]
        });
    }, [dateLocale, range, report.comparisons, report.events.length, report.summary, statsService, t, timezone]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const columns = useMemo(() => [
        {
            title: t('BUTTON_VIEW'),
            width: 72,
            fixed: 'left',
            render: (_, row) => (
                <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    disabled={!row?.alertId || !row?.phone}
                    title={t('BUTTON_VIEW')}
                    aria-label={t('BUTTON_VIEW')}
                    onClick={event => {
                        event.stopPropagation();
                        openWatch(row);
                    }}
                />
            )
        },
        {
            title: t('PAP_REPORT_COLUMN_EVENT_DATE'),
            dataIndex: 'eventAt',
            width: 170,
            render: value => formatDateTime(value, timezone, dateLocale)
        },
        {
            title: t('PAP_REPORT_COLUMN_ALERT_DATE'),
            dataIndex: 'alertId',
            width: 170,
            render: value => formatDateTime(Number(value), timezone, dateLocale)
        },
        {
            title: t('PAP_REPORT_COLUMN_COLLECTION'),
            dataIndex: 'coll',
            width: 110,
            render: value => <Tag>{value}</Tag>
        },
        {
            title: t('LABEL_PHONE'),
            dataIndex: 'phone',
            width: 140
        },
        {
            title: t('ALERT_STATS_COLUMN_ID'),
            dataIndex: 'alertId',
            width: 170
        },
        {
            title: t('PAP_REPORT_COLUMN_GROUP'),
            dataIndex: 'eventGroup',
            width: 150,
            render: value => <Tag>{translateKey(t, eventGroups[value], value)}</Tag>
        },
        {
            title: t('PAP_REPORT_COLUMN_PATH'),
            dataIndex: 'eventType',
            width: 240,
            render: (_, row) => <Tag color={row.eventColor}>{translateKey(t, eventLabels[row.eventType], row.eventLabel)}</Tag>
        },
        {
            title: t('ALERT_STATS_COLUMN_RESULT'),
            width: 180,
            render: (_, row) => resultTag(row, t)
        },
        {
            title: t('PAP_REPORT_COLUMN_POLICIES'),
            width: 180,
            render: (_, row) => policyTag(row, t)
        },
        {
            title: t('PAP_REPORT_COLUMN_TRACEABILITY'),
            width: 170,
            render: (_, row) => traceabilityTag(row, t)
        },
        {
            title: t('PAP_REPORT_COLUMN_DETAIL'),
            dataIndex: 'detail',
            width: 320,
            render: value => value ? <Text style={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}>{value}</Text> : '-'
        },
        {
            title: t('PAP_REPORT_COLUMN_MAIL_TYPE'),
            width: 230,
            render: (_, row) => translateKey(t, mailTypeLabels[row.eventMailType], row.eventMailType || '-')
        },
        {
            title: t('PAP_REPORT_COLUMN_MAIL_STATUS'),
            width: 170,
            render: (_, row) => row.hasMail ? (
                <Tag color={tagColors[row.mailStatus?.status] || 'default'}>
                    {mailStatusLabel(row, t)}
                </Tag>
            ) : '-'
        },
        {
            title: t('PAP_REPORT_COLUMN_MAIL_DATE'),
            width: 170,
            render: (_, row) => formatDateTime(row.mailStatus?.at || row.eventMailQueuedAt, timezone, dateLocale)
        },
        {
            title: t('PAP_REPORT_COLUMN_MAIL_ID'),
            dataIndex: 'eventMailId',
            width: 220,
            render: value => value || '-'
        }
    ], [dateLocale, openWatch, t, timezone]);

    const tablePaginationConfig = useMemo(() => ({
        current: tablePagination.current,
        pageSize: tablePagination.pageSize,
        showSizeChanger: true,
        //aqui se indican las opciones de paginación, se puede modificar
        pageSizeOptions: ['10', '25', '50', '100'],
        showTotal: total => t('ALERT_STATS_TABLE_TOTAL', { total }),
        onChange: (current, pageSize) => {
            setTablePagination(prev => ({
                current: prev.pageSize === pageSize ? current : 1,
                pageSize
            }));
        }
    }), [t, tablePagination]);

    if (!canAccess) {
        return (
            <div className="template-container">
                <Result
                    status="403"
                    title={t('ALERT_STATS_FORBIDDEN_TITLE')}
                    subTitle={t('ALERT_STATS_FORBIDDEN_SUBTITLE')}
                />
            </div>
        );
    }

    return (
        <div className="template-container" style={pageStyle}>
            <Space align="center" wrap size={[16, 12]} style={{ marginBottom: 8 }}>
                <RangePicker
                    value={range}
                    onChange={setRange}
                    allowClear={false}
                    format="YYYY-MM-DD"
                />
                <Button type="primary" icon={<ReloadOutlined />} onClick={loadReport} loading={loading}>
                    {t('ALERT_STATS_LOAD_BUTTON')}
                </Button>
                <Text type="secondary">{t('LABEL_SERVICE')}: {statsService.label}</Text>
            </Space>

            <Alert
                type={includesHistoricalRange ? 'warning' : 'info'}
                showIcon
                message={t('ALERT_STATS_TRACE_NOTICE_TITLE', { date: TRACE_START_LABEL })}
                description={(
                    <Space direction="vertical" size={6}>
                        <Text>{t('ALERT_STATS_TRACE_NOTICE_DESC')}</Text>
                        <Text>{t('ALERT_STATS_INTERVAL_NOTICE_DESC')}</Text>
                    </Space>
                )}
            />

            {error && <Alert type="error" showIcon message={error} />}

            <Spin spinning={loading}>
                <ComparisonSection comparisons={report.comparisons || []} t={t} />
                {sections.map((section, index) => (
                    <MetricSection
                        key={section.titleKey}
                        title={t(section.titleKey)}
                        items={section.items}
                        summary={report.summary || {}}
                        t={t}
                        marginTop={index === 0 ? 50 : 22}
                        help={sectionHelp[section.helpKey]}
                    />
                ))}

                <div style={eventHeaderStyle}>
                    <Title level={4} style={{ margin: 0 }}>{t('ALERT_STATS_EVENTS_TITLE')}</Title>
                    <Space wrap>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadSummaryCsv}
                            disabled={!report.events.length}
                            style={exportButtonStyle}
                        >
                            {t('ALERT_STATS_DOWNLOAD_SUMMARY_CSV')}
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadCsv}
                            disabled={!report.events.length}
                            style={exportButtonStyle}
                        >
                            {t('ALERT_STATS_DOWNLOAD_CSV')}
                        </Button>
                    </Space>
                </div>
                {report.events.length ? (
                    <Table
                        size="small"
                        columns={columns}
                        dataSource={report.events}
                        rowKey="rowKey"
                        tableLayout="fixed"
                        scroll={{ x: 3070 }}
                        pagination={tablePaginationConfig}
                    />
                ) : (
                    <Empty description={t('ALERT_STATS_EMPTY')} />
                )}
            </Spin>
        </div>
    );
}

export default AlertStatsReport;
